package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Page("/kmm/room")
@Composable
fun KMPRoomPage() {
    BlogLayout(
        PageProperties(
            "Room Database",
            ""
        ),
        BlogProperties("KMP", "Room Database")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Setting up dependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    room = "2.7.0-alpha11"
                    sqlite = "2.5.0-alpha11"
                    
                    [libraries]
                    androidx-room-compiler = { group = "androidx.room", name = "room-compiler", version.ref = "room" }
                    androidx-room-runtime = { group = "androidx.room", name = "room-runtime", version.ref = "room" }
                    sqlite-bundled = { module = "androidx.sqlite:sqlite-bundled", version.ref = "sqlite" }
                    
                    [plugins]
                    room = { id = "androidx.room", version.ref = "room" }
                 
                    plugins {
                        alias(libs.plugins.room)
                    }
                    
                    sourceSets {
                        commonMain.dependencies {
                            implementation(libs.androidx.room.runtime)
                            implementation(libs.sqlite.bundled)
                        }
                    }
                    
                    room {
                        schemaDirectory("$projectDir/schema")
                    }
                    """.trimIndent()
            )

            //common
            KotlinCode(
                code = """
                    //iosMain
                    
                    //IosPlatformModule.kt
                    import androidx.room.Room
                    import androidx.sqlite.driver.bundled.BundledSQLiteDriver
                    import io.github.vkindl.notes.core.data.database.NotesDatabase
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO
                    import org.koin.dsl.module
                    import platform.Foundation.NSDocumentDirectory
                    import platform.Foundation.NSFileManager
                    import platform.Foundation.NSUserDomainMask

                    actual val platformModule = module {
                        single<NotesDatabase> { createNotesDatabase() }
                    }

                    private fun createNotesDatabase(): NotesDatabase {
                        val dbFile = getDocumentDirectory() + "/notes.db"
                        return Room.databaseBuilder<NotesDatabase>(
                            name = dbFile,
                        ).setDriver(BundledSQLiteDriver())
                            .setQueryCoroutineContext(Dispatchers.IO)
                            .build()
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun getDocumentDirectory(): String {
                        val documentDirectory = NSFileManager.defaultManager.URLForDirectory(
                            directory = NSDocumentDirectory,
                            inDomain = NSUserDomainMask,
                            appropriateForURL = null,
                            create = false,
                            error = null
                        )
                        return requireNotNull(documentDirectory?.path)
                    }
                    
                    //androidMain
                    
                    //AndroidPlatformModule.kt
                    import android.content.Context
                    import androidx.room.Room
                    import androidx.sqlite.driver.bundled.BundledSQLiteDriver
                    import io.github.vkindl.notes.core.data.database.NotesDatabase
                    import kotlinx.coroutines.Dispatchers
                    import org.koin.dsl.module

                    actual val platformModule = module {
                        single<NotesDatabase> { createNotesDatabase(get()) }
                    }

                    private fun createNotesDatabase(context: Context): NotesDatabase {
                        val appContext = context.applicationContext
                        val dbFile = appContext.getDatabasePath("notes.db")

                        return Room.databaseBuilder(
                            context,
                            NotesDatabase::class.java,
                            dbFile.absolutePath
                        ).setDriver(BundledSQLiteDriver())
                            .setQueryCoroutineContext(Dispatchers.IO)
                            .build()
                    }
                    """.trimIndent()
            )

            //di
            KotlinCode(
                code = """
                    //initKoin.kt
                    import org.koin.core.context.startKoin
                    import org.koin.dsl.KoinAppDeclaration

                    fun initKoin(config: KoinAppDeclaration? = null) {
                        startKoin {
                            config?.invoke(this)
                            modules(commonModule, platformModule)
                        }
                    }
                    
                    //CommonModule.kt
                    expect val platformModule: Module

                    val commonModule = module {
                        single { get<NotesDatabase>().notesDao() }
                        single<NotesLocalDataSource> { NotesLocalDataSourceImpl(get()) }
                        single<NotesRepository> { NotesRepositoryImpl(get()) }

                        factory { ObserveNotesUseCase(get()) }
                        factory { ObserveNoteUseCase(get()) }
                        factory { SaveNoteUseCase(get()) }
                        factory { DeleteNoteUseCase(get()) }

                        viewModelOf(::NotesViewModel)
                        viewModelOf(::NoteDetailViewModel)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //NoteEntity
                    @Entity(tableName = "note")
                    data class NoteEntity(
                        @PrimaryKey(autoGenerate = true) val id: Int = 0,
                        @ColumnInfo(name = "title") val title: String,
                        @ColumnInfo(name = "description") val description: String?
                    )
                    
                    //NoteMapper
                    fun NoteEntity.toDomain(): Note {
                        return Note(
                            id = id,
                            title = title,
                            description = description
                        )
                    }

                    fun Note.toEntity(): NoteEntity {
                        return NoteEntity(
                            id = id,
                            title = title,
                            description = description
                        )
                    }
                    
                    //NotesDao.kt
                    @Dao
                    interface NotesDao {

                        @Query("SELECT * FROM note")
                        fun observeAllNotes(): Flow<List<NoteEntity>>

                        @Query("SELECT * FROM note WHERE id = :id")
                        fun observeNoteById(id: Int): Flow<NoteEntity?>

                        @Insert(onConflict = OnConflictStrategy.REPLACE)
                        suspend fun insertNote(note: NoteEntity)

                        @Query("DELETE FROM note WHERE id = :id")
                        suspend fun deleteNoteById(id: Int)
                    }
                    
                    //NotesDatabase.kt
                    import androidx.room.ConstructedBy
                    import androidx.room.Database
                    import androidx.room.RoomDatabase
                    import androidx.room.RoomDatabaseConstructor

                    @Database(entities = [NoteEntity::class], version = 1)
                    @ConstructedBy(NotesDatabaseConstructor::class)
                    abstract class NotesDatabase : RoomDatabase() {
                        abstract fun notesDao(): NotesDao
                    }

                    @Suppress("NO_ACTUAL_FOR_EXPECT")
                    internal expect object NotesDatabaseConstructor : RoomDatabaseConstructor<NotesDatabase>
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //NotesLocalDataSource.kt
                    interface NotesLocalDataSource {
                        fun observeAllNotes(): Flow<List<NoteEntity>>
                        fun observeNoteById(id: Int): Flow<NoteEntity?>
                        suspend fun insertNote(note: NoteEntity)
                        suspend fun deleteNoteById(id: Int)
                    }
                    
                    //NotesLocalDataSourceImpl.kt
                    class NotesLocalDataSourceImpl(
                        private val notesDao: NotesDao
                    ) : NotesLocalDataSource {

                        override fun observeAllNotes(): Flow<List<NoteEntity>> {
                            return notesDao.observeAllNotes()
                        }

                        override fun observeNoteById(id: Int): Flow<NoteEntity?> {
                            return notesDao.observeNoteById(id)
                        }

                        override suspend fun insertNote(note: NoteEntity) {
                            return notesDao.insertNote(note)
                        }

                        override suspend fun deleteNoteById(id: Int) {
                            return notesDao.deleteNoteById(id)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class NotesRepositoryImpl(
                        private val notesLocalDataSource: NotesLocalDataSource
                    ) : NotesRepository {

                        override fun observeAllNotes(): Flow<List<Note>> {
                            return notesLocalDataSource.observeAllNotes().map { notes ->
                                notes.map { entity ->
                                    entity.toDomain()
                                }
                            }
                        }

                        override fun observeNoteById(id: Int): Flow<Note?> {
                            return notesLocalDataSource.observeNoteById(id).map { entity ->
                                entity?.toDomain()
                            }
                        }

                        override suspend fun insertNote(note: Note) {
                            return notesLocalDataSource.insertNote(note.toEntity())
                        }

                        override suspend fun deleteNoteById(id: Int) {
                            notesLocalDataSource.deleteNoteById(id)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class DeleteNoteUseCase(
                        private val notesRepository: NotesRepository
                    ) {
                        suspend operator fun invoke(id: Int) {
                            notesRepository.deleteNoteById(id)
                        }
                    }
                    
                    class ObserveNotesUseCase(
                        private val notesRepository: NotesRepository
                    ) {
                        operator fun invoke(): Flow<List<Note>> {
                            return notesRepository.observeAllNotes()
                        }
                    }
                    
                    class NotesViewModel(
                        private val deleteNoteUseCase: DeleteNoteUseCase,
                        observeNotesUseCase: ObserveNotesUseCase
                    ) : ViewModel() {

                        val state = observeNotesUseCase().map { notes ->
                            if (notes.isEmpty()) NotesUiState.EMPTY else NotesUiState.NOTES(notes)
                        }.stateIn(
                            viewModelScope,
                            SharingStarted.WhileSubscribed(5_000),
                            NotesUiState.EMPTY
                        )

                        fun deleteNote(id: Int) {
                            viewModelScope.launch {
                                deleteNoteUseCase(id)
                            }
                        }

                        sealed interface NotesUiState {
                            data object EMPTY : NotesUiState
                            data class NOTES(val notes: List<Note>) : NotesUiState
                        }
                    }
                    """.trimIndent()
            )
        }

        KotlinCode(
            code = $$"""
                //1 
                import androidx.datastore.core.DataStore
                import androidx.datastore.preferences.core.Preferences
                import com.ihsanarslan.datastorekoforkotlinmultiplatform.data.local.DATA_STORE_FILE_NAME
                import com.ihsanarslan.datastorekoforkotlinmultiplatform.data.local.createDataStore
                import kotlinx.cinterop.ExperimentalForeignApi
                import platform.Foundation.NSDocumentDirectory
                import platform.Foundation.NSFileManager
                import platform.Foundation.NSUserDomainMask
                import kotlin.plus

                @OptIn(ExperimentalForeignApi::class)
                fun createDataStore(): DataStore<Preferences> {
                    return createDataStore {
                        val documentDirectory = NSFileManager.defaultManager.URLForDirectory(
                            directory = NSDocumentDirectory,
                            inDomain = NSUserDomainMask,
                            appropriateForURL = null,
                            create = false,
                            error = null,
                        )
                        requireNotNull(documentDirectory).path + "/$DATA_STORE_FILE_NAME "
                    }
                }
            """.trimIndent()
        )
    }
}

//single<MyDao>{ myDataBaseInstance.myDao() } - bad
//single<() -> MyDao>{ {myDataBaseInstance.myDao()} } - good
//single<() -> MyDao>(named("MyDao")){ {myDataBaseInstance.myDao()} }
//https://github.com/Nacchofer31/RandomBoxd
//https://github.com/seabat/ramen-note (Camera/Gallery Permission Best, Image Save, Fetch, Rename and Delete, Room Database Latest)
//https://github.com/XDdevv/VocabularyFlash.git (Desktop, Mobile, Room Database Latest)
//https://github.com/krbns/Calory
//https://dev.to/programmingcentral/beyond-keyword-search-building-a-local-vector-database-on-android-with-room-and-gemini-nano-1m3d